/* eslint-disable no-param-reassign */
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { AlertType } from "../../constants";
import PictureGallery from "./PictureGallery";
import SubscriptionLimitClue from "../Subscriptions/SubscriptionLimitClue";
import FilesUpload from "../Files/FilesUpload";
import { InputAlert } from "../Form";
import { DeleteActionButton } from "../ActionBar";
import { scaleImageFile } from "../../utils/imageUtils";
import { extractPictures } from "../../extractors";
import { sessionShape } from "../../shapes";
import { useApi, useSubscriptionQuery } from "../../hooks";
import { withSession } from "../../context";
import Loader from "../Loader";

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const MAX_PICTURE_SIZE = 1024 * 1024;

type Props = {
  model: string;
  modelId: number;
  placeholder?: string;
  session: sessionShape;
};

export function EditPictures({ model, modelId, session, placeholder }: Props) {
  const intl = useIntl();
  const api = useApi();
  const queryClient = useQueryClient();
  // We use isSuccess as a workaround for the ID App, in which the subscription query returns undefined
  const { data: subscription, isSuccess: isSubscriptionSuccess } =
    useSubscriptionQuery();

  const [rejectFiles, setRejectFiles] = useState();
  const [selectedPicture, setSelectedPicture] = useState();
  const [currentPictureIndex, setCurrentPictureIndex] = useState(-1);
  const fileStorageLimit =
    model !== "products"
      ? (session as $TSFixMe)?.subscriptionStorageQuota
      : Number.POSITIVE_INFINITY;

  const calculatePictureIndex = (picturesData: $TSFixMe, pictureId: $TSFixMe) =>
    picturesData.findIndex((picture: $TSFixMe) => picture.id === pictureId);

  const { data, isLoading } = api.get.useQuery(
    `/${model}/${modelId}/pictures`,
    null,
    {
      onSuccess: (response: $TSFixMe) => {
        if (currentPictureIndex > -1) {
          const pictureIndex = calculatePictureIndex(
            response.pictures,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            selectedPicture.id,
          );
          /* istanbul ignore next */
          if (pictureIndex === -1) {
            if (response.pictures.length > currentPictureIndex) {
              setSelectedPicture(response.pictures[currentPictureIndex]);
            } else if (response.pictures.length > 0) {
              setSelectedPicture(response.pictures[response.pictures.length]);
            } else {
              // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
              setSelectedPicture(null);
              setCurrentPictureIndex(-1);
            }
          }
        }
      },
    },
  );

  const pictures = data ? extractPictures(data) : null;

  const onPictureChange = (picture: $TSFixMe) => {
    if (picture && picture.id !== (selectedPicture as $TSFixMe)?.id) {
      setSelectedPicture(picture);
      setCurrentPictureIndex(calculatePictureIndex(pictures, picture.id));
    }
  };

  const updatePicturesAndFileStorage = async () => {
    queryClient.removeQueries({ queryKey: [`/${model}/${modelId}/pictures`] });
    queryClient.invalidateQueries({ queryKey: ["/subscription"] });
  };

  const selectedPictureId = selectedPicture
    ? (selectedPicture as $TSFixMe).id
    : pictures?.[0]?.id;
  const { mutate: deletePicture, isLoading: isDeletingPicture } =
    api.delete.useMutation(
      `/${model}/${modelId}/pictures/${selectedPictureId}`,
      {
        onSuccess: async () => {
          await updatePicturesAndFileStorage();
        },
      },
    );

  const onConfirmDelete = async () => {
    deletePicture();
  };

  const { mutateAsync: uploadPicture, isLoading: isUploadingPicture } =
    api.upload.useMutation(`/${model}/${modelId}/pictures`, {
      onSuccess: async () => {
        await updatePicturesAndFileStorage();
      },
      onError: (error: $TSFixMe) => {
        throw error;
      },
    });

  const uploadPictures = async (files: $TSFixMe) => {
    let currentFileStorage = subscription?.usage?.file_storage || 0;
    const rejectedFiles = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const file of files) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        file.uploadError = "incompatible_type_error";
        rejectedFiles.push(file);
      } else {
        // eslint-disable-next-line no-await-in-loop
        const compressedImageFile = await scaleImageFile(file, 1200, 0.8);
        if ((compressedImageFile as $TSFixMe).size > MAX_PICTURE_SIZE) {
          file.uploadError = "size_limit_exceeded";
          rejectedFiles.push(file);
        } else if (
          (compressedImageFile as $TSFixMe).size + currentFileStorage >
          fileStorageLimit
        ) {
          file.uploadError = "storage_limit_exceeded";
          rejectedFiles.push(file);
        } else {
          try {
            // eslint-disable-next-line no-await-in-loop
            await uploadPicture({ image: compressedImageFile });
            currentFileStorage += (compressedImageFile as $TSFixMe).size;
          } catch (error) {
            file.uploadError = (error as $TSFixMe).message;
            rejectedFiles.push(file);
          }
        }
      }
    }
    // @ts-expect-error TS(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
    setRejectFiles(rejectedFiles);
  };

  const onDrop = async (acceptedFiles: $TSFixMe) => {
    const acceptedFilesWithPreview = acceptedFiles.map((file: $TSFixMe) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    );

    await uploadPictures(acceptedFilesWithPreview);
  };

  const spinner = (
    <Loader
      loading={isLoading || isUploadingPicture || isDeletingPicture}
      withText={false}
      className="pictures-spinner"
    />
  );

  const pictureDelete =
    pictures && pictures.length > 0 ? (
      <div className="picture-delete">
        <DeleteActionButton
          id="delete-picture"
          modalTitle={intl.formatMessage({
            id: "edit_pictures.delete.modal_title",
          })}
          modalMessage={intl.formatMessage({
            id: "edit_pictures.delete.modal_message",
          })}
          onConfirm={onConfirmDelete}
        />
      </div>
    ) : null;

  const rejectedFilesRendered = rejectFiles
    ? (rejectFiles as $TSFixMe).map((file: $TSFixMe) => {
        switch (file.uploadError) {
          case "size_limit_exceeded":
            file.uploadErrorMessage = intl.formatMessage({
              id: "edit_pictures.size_error",
            });
            break;
          case "storage_limit_exceeded":
            file.uploadErrorMessage = intl.formatMessage({
              id: "edit_pictures.size_exceeds_limit",
            });
            break;
          default:
            file.uploadErrorMessage = intl.formatMessage({
              id: "edit_pictures.incompatible_type_error",
            });
        }
        return (
          <li key={file.name}>
            <strong>{file.name}</strong>
            {`: ${file.uploadErrorMessage}`}
          </li>
        );
      })
    : null;

  const rejectionError =
    rejectFiles && (rejectFiles as $TSFixMe).length > 0 ? (
      <div className="pictures-rejection">
        <InputAlert
          message={intl.formatMessage({
            id: "edit_pictures.dropzone.rejection",
          })}
          type={AlertType.ERROR}
        >
          <ul>{rejectedFilesRendered}</ul>
        </InputAlert>
        <SubscriptionLimitClue />
      </div>
    ) : null;

  return pictures && isSubscriptionSuccess ? (
    <>
      <div className="pictures-header">
        <h2>
          <FormattedMessage id="edit_pictures.header" />
        </h2>
      </div>
      <div className="edit-pictures">
        <PictureGallery
          pictures={pictures}
          placeholder={placeholder}
          onPictureChange={onPictureChange}
        >
          {pictureDelete}
          {spinner}
        </PictureGallery>
        <FilesUpload
          // @ts-expect-error TS(2322) FIXME: Type '{ id: string; classNamePrefix: string; dropz... Remove this comment to see the full error message
          id="pictures-upload"
          classNamePrefix="picture"
          dropzoneInfoPrefix="edit_pictures"
          onDrop={onDrop}
        />
      </div>
      {rejectionError}
    </>
  ) : null;
}

export default withSession(EditPictures);
